import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ContextMenu, Item, MenuButton, WithTooltip } from 'basic-components';
import { Exit, Info } from 'basic-components/icons';
import { DataScienceEnums } from 'dashboard-services/api/dataScience';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions, loginActions, statusBoxActions, StatusIcon } from 'primary-components';

import { basicCustomInsightActions, basicCustomInsightConsts, diagramCustomInsightActions } from 'actions/modules/insight';
import getApiConfig from 'api/ApiConfig';
import { pipelinesQueries } from 'queries/modules/datascience';
import { routing } from 'routing';
import { customInsightSelectors } from 'selectors/modules/insight/custom';
import { getTriggerTypeIcon } from 'services/TriggerType';

import StatusBox from 'components/modules/DataScience/PipelineCreator/Body/Workspace/StatusBox';

import { PortWidget } from '../../Port';
import NodeModel from '../NodeModel';
import ConnectionBox from './ConnectionBox';
import NodeCircle from './NodeCircle';
import ParamBox from './ParamBox';
import PreviewAttributeAction from './PreviewAttributeAction';

import { useQuery } from '@tanstack/react-query';
import parser from 'cron-parser';
import { When } from 'react-if';

import classNames from 'classnames';
import './NodeWidget.scss';

const getAction = (param, onCronValidationError)  => {
  let text = ""
  if(param.type === DataScienceEnums.PIPELINE_TRIGGER_TYPE.EMAIL) {
    text = `Triggers on email received by ${param.value}.`
  } else if(param.type === DataScienceEnums.PIPELINE_TRIGGER_TYPE.CRON) {
    try {
      const interval = parser.parseExpression(param.value, { tz: TimeService.UTC });
      text = (
        <Fragment>
          <p>{param.value}</p>
          <p>Next run at: {TimeService.getUserFormattedDateTime(interval.next(), TimeService.UTC, "YYYY-MM-DD HH:mm")}</p>
        </Fragment>
      )
    } catch {
      onCronValidationError()
      text = "Could not translate the cron expression"
    }
  } else if(param.type === DataScienceEnums.PIPELINE_TRIGGER_TYPE.PIPELINE) {
    text = `Triggers when pipeline ${param.value} sucessfully finishes.`
  } else if(param.type === DataScienceEnums.PIPELINE_TRIGGER_TYPE.FILE_ARRIVAL) {
    text = `Triggers on file uploads matching ${param.value}.`
  }
  return (
    <WithTooltip
        text={text}
    >
      <div className="insight-node-widget__param__content__action__wrapper">
        <Info
            color="blue-bright"
            height={12}
            width={12}
        />
      </div>
    </WithTooltip>
  )
}

const getAttributeAction = attribute => {
  if(attribute.action === basicCustomInsightConsts.AVAILABLE_ACTIONS.PREVIEW) {
    return (
      <PreviewAttributeAction
          {...attribute}
      />
    )
  }
  return <Fragment/>
}
const useGetTriggers = ({ reduxNodeObj, title }) => {
  const dispatch = useDispatch(),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withShouldDisplayError(false)
            .withOnError(({ message }) => (
              dispatch(statusBoxActions.addItem({ message, title, variant: StatusesEnums.STATUS.WARN }))
            ))
        , [dispatch, title]),
        { data: triggersRaw } = pipelinesQueries.useGetPipeline({
          getQueryBuilder,
          groupName: reduxNodeObj?.pipeline?.groupName,
          pipelineName: reduxNodeObj?.pipeline?.name,
          version: "latest",
          enabled: reduxNodeObj?.withImportTriggers && !!reduxNodeObj?.pipeline,
          refetchInterval: () => shouldBeLimited() ? undefined : 10 * 60_000, // 10m
        }),
        triggers = useMemo(() => {
          if(!triggersRaw) {
            return undefined;
          }
          const triggerParams = triggersRaw.batchConfig.tasks.filter(task => task.taskName.includes("trigger")).map(task => task.taskOverrides)
          const toSet = []
          triggerParams.forEach(({ params: taskParams }) => {
            if(taskParams["Email to"]) {
              toSet.push({
                value: taskParams["Email to"],
                type: DataScienceEnums.PIPELINE_TRIGGER_TYPE.EMAIL
              })
            } else if(taskParams["Cron"]) {
              toSet.push({
                value: taskParams["Cron"],// + ` (${taskParams["Timezone"]})`,
                type: DataScienceEnums.PIPELINE_TRIGGER_TYPE.CRON
              })
            } else if(taskParams["Pipeline"]) {
              toSet.push({
                value: JSON.parse(taskParams["Pipeline"])?.name,
                type: DataScienceEnums.PIPELINE_TRIGGER_TYPE.PIPELINE
              })
            } else if(taskParams["File Regex"]) {
              toSet.push({
                value: taskParams["File Regex"] + " in group " + taskParams["Group"],
                type: DataScienceEnums.PIPELINE_TRIGGER_TYPE.FILE_ARRIVAL
              })
            }
          })
          return toSet;
        }, [triggersRaw])
  return triggers;
}
const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
const EMPTY_FUN = () => {}
const onSelect = response => response.items?.[0]
const useGetStatus = ({ getQueryBuilder, reduxNodeObj, shouldBeLimited }) => useQuery({
  ...StatusesQuery.QUERIES.search(
    getQueryBuilder,
    {
      query: "*",
      size: 1,
      from: 0,
      aggregate: true,
      type: StatusesEnums.STATUS_TYPE.PIPELINE,
      pipelineRootIds: [reduxNodeObj?.pipeline?.root]
    }
  ),
  enabled: !!reduxNodeObj?.withImportStatuses && !!reduxNodeObj?.pipeline,
  refetchInterval: () => shouldBeLimited() ? undefined : 5000,
  getNextPageParam: CommonService.getNextPageParam,
  select: onSelect
})
const useStatus = ({ reduxNodeObj }) => {
  const dispatch = useDispatch(),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withOnError(EMPTY_FUN)
        , [dispatch]),
        { data: status } = useGetStatus({ getQueryBuilder, reduxNodeObj, shouldBeLimited })
  return status;
}

const { useFromInsightTile, useFromCustomState } = customInsightSelectors
const NodeWidget = ({ engine, node }) => {
  const dispatch = useDispatch(),
        tileIndex = node.getTileIndex(),
        title = useFromInsightTile({ tileIndex, paramName: "title" }),
        nodes = useFromInsightTile({ tileIndex, paramName: "nodes", defaultValue: EMPTY_ARRAY }),
        doubleClickedNode = useFromCustomState("doubleClickedNode"),
        reduxNodeObj = nodes?.[node?.getId?.()] || EMPTY_OBJ,
        
        [statusOpen, setStatusOpen] = useState(false),
        status = useStatus({ reduxNodeObj }),
        
        onCronValidationError = useCallback(() => {
          dispatch(statusBoxActions.addItem({ message: "Could not translate the cron expression", title, variant: StatusesEnums.STATUS.WARN }))
        }, [dispatch, title]),
        openStatusBox = useCallback(e => {
          e.preventDefault()
          e.stopPropagation()
          setStatusOpen(open => !open)
        }, []),
        closeStatusBox = useCallback(() => setStatusOpen(false), []),
        onContextMenu = useCallback(e => {
          e.preventDefault()
          e.stopPropagation()
          div.current.click()
        }, []),
        getReduxNode = useCallback(() => dispatch(diagramCustomInsightActions.getReduxNodeFromState({ nodeId: node.getId(), index: node.getTileIndex() })), [dispatch, node]),
        setSelectedReduxNode = useCallback(() => {
          dispatch(basicCustomInsightActions.onChange(node.getId(), "doubleClickedNode"))
          dispatch(basicCustomInsightActions.onChange(basicCustomInsightConsts.ACTIVE_PANEL.NODE_DETAILS, "activePanel"))
        }, [dispatch, node]),
        copyNode = useCallback(() => {
          navigator.clipboard.writeText(JSON.stringify({ tasks: [getReduxNode()], links: [] }))
            .then(() => dispatch(globalActions.getMessageStore()).success(`${reduxNodeObj?.label} copied to the clipboard.`))
        }, [dispatch, getReduxNode, reduxNodeObj?.label]),
        deleteNode = useCallback(() => {
          dispatch(globalActions.getToastsStore()).info(
            (reduxNodeObj?.label || "This node") + " will be deleted immediately.",
            "Delete canvas node",
            {
              withCancel: false,
              confirmText: "Delete",
              confirmAction: () => {
                node.remove()
                engine.repaintCanvas();
              },
            }
          );
        }, [dispatch, engine, node, reduxNodeObj?.label]),
        openItem = useCallback(() => {
          switch(reduxNodeObj?.type) {
            default:
            case basicCustomInsightConsts.DIAGRAM_NODES.PIPELINE.type: {
              CommonService.openInNewTab(DashboardLinkFactory.dataScience.pipeline({ groupName: reduxNodeObj?.pipeline?.groupName, jobId: status?.jobId || undefined, pipelineName: reduxNodeObj?.pipeline.name }))
              break;
            }
          }
        }, [reduxNodeObj, status]),
        triggers = useGetTriggers({ reduxNodeObj, title }),
        div = useRef(),
        hasStatusAccess = useMemo(() => dispatch(loginActions.hasAccessTo(routing.statuses.name)), [dispatch]),
        inputPort = useMemo(() => Object.entries(node.ports).find(([portName]) => portName === "ConnectionInput")?.[1], [node?.ports]),
        outputPort = useMemo(() => Object.entries(node.ports).find(([portName]) => portName === "ConnectionOutput")?.[1], [node?.ports]),
        menuButton = useMemo(() => (<MenuButton key="MenuButton"><div ref={div}/></MenuButton>), []),
        namePortRef = useRef()

  return (
    <div 
        className={classNames(
          "insight-node-widget", 
          { "insight-node-widget--is-selected": node.getId() === doubleClickedNode }
        )}
        onContextMenu={onContextMenu}
        onDoubleClick={setSelectedReduxNode}
        style={{ width: `${reduxNodeObj?.nodeWidth || 160}px` }}
    >
      {statusOpen && hasStatusAccess && (
        <StatusBox 
            close={closeStatusBox}
            defaultStatuses={status?.componentStatuses}
            nodeId={node?.getId()}
            startTime={status?.startTime}
            taskName={reduxNodeObj?.label}
        />
      )}
      <NodeCircle
          diagramNodeType={reduxNodeObj?.type}
      />
      <When condition={reduxNodeObj?.withOpenButton && reduxNodeObj?.pipeline}>
        {() => (
          <div 
              className="insight-node-widget__open-button"
              onClick={openItem}
          >
            <Exit
                color="blue-bright"
                height={16}
                width={16}
            />
          </div>
        )}
      </When>
      <When condition={reduxNodeObj?.withImportStatuses && reduxNodeObj?.pipeline}>
        {() => (
          <div 
              className={classNames("insight-node-widget__status", { "insight-node-widget__status--is-active": statusOpen })}
              onClick={openStatusBox}
          >
            <StatusIcon
                status={status?.status}
                warnStatuses={status?.warnStatuses}
                withAnimation
            />
          </div>
        )}
      </When>
      <ContextMenu  menuButton={menuButton}>
        <Item onClick={setSelectedReduxNode}>
          Edit
        </Item>
        <Item onClick={copyNode}>
          Copy
        </Item>
        <Item onClick={deleteNode}>
          Delete
        </Item>
      </ContextMenu>
      <div className="insight-node-widget__ports">
        <PortWidget
            engine={engine}
            inputPort={inputPort}
            key={node.options.name + "ConnectionPort"}
            outputPort={outputPort}
            ref={namePortRef}
            text={reduxNodeObj?.label || reduxNodeObj?.type}
            tileIndex={node.getTileIndex()}
            width={reduxNodeObj?.nodeWidth ? `${reduxNodeObj?.nodeWidth}px` : undefined}
        />
        {(reduxNodeObj?.withImportTriggers ? (triggers || []) : []).map((param, paramIndex) => (
          <ParamBox
              action={getAction(param, onCronValidationError)}
              icon={getTriggerTypeIcon(param.type, "grey-dark", 12)}
              key={param.name + param.value + "Trigger" + paramIndex}
              text={param.value}
              width={reduxNodeObj?.nodeWidth ? `${reduxNodeObj?.nodeWidth}px` : undefined}
          />
        ))}
        {(reduxNodeObj?.attributes || []).map((param, paramIndex) => (
          <ParamBox
              action={getAttributeAction(param)}
              key={node.options.name + param.label + "Attribute" + paramIndex}
              text={param.label}
              width={reduxNodeObj?.nodeWidth ? `${reduxNodeObj?.nodeWidth}px` : undefined}
          />
        ))}
        {(reduxNodeObj?.connections || []).map((connection, connectionIndex) => (
          <ConnectionBox
              {...connection}
              id={connection?.id}
              key={"Connection" + connectionIndex}
              tile={connection.tile}
              width={reduxNodeObj?.nodeWidth ? `${reduxNodeObj?.nodeWidth}px` : undefined}
          />
        ))}
      </div>
    </div>
  )
}

NodeWidget.propTypes = {
  engine: PropTypes.object.isRequired,
  node: PropTypes.instanceOf(NodeModel).isRequired,
}

export default NodeWidget;
